import { CustomerOrderSummary } from "@/components/CustomerOrderSummary";
import { customerItemTotal } from "../../../../lib/ordering-pricing";
import { ChatImageComposer, MessageImages } from "@/components/MessageImages";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { orderingPaymentAppearance } from "@/lib/orderingAppearance";
import { useEffect, useRef, useState } from "react";
import { useRoute, Link } from "wouter";
import { Truck, MessageSquare, X, Send, CheckCircle2, Package, Clock, Star, Heart } from "lucide-react";
import DriverLocationMap from "@/components/DriverLocationMap";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe as useStripeEl, useElements } from "@stripe/react-stripe-js";
import { DeliveryCodeCard } from "@/components/hub";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ayrshire_token");
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }

type OrderDetail = {
  id: number; status: string; fulfilmentType: string;
  totalPence: number; customerPaidPence?: number | null; subtotalPence?: number; deliveryFeePence?: number; serviceChargePence?: number | null; bagChargePence?: number | null; customerPlatformFeePence?: number; customerHubDriverFeePence?: number; customerUpliftMarginPence?: number | null; offerDiscountPence?: number; offerTitle?: string | null; deliveryAddress: string | null;
  createdAt: string; driverMode: string | null; deliveryCode?: string | null;
  isEndToEndOrder?: boolean | null;
  leg2DeliveryFeePence?: number | null;
  pledgedTipPence?: number | null;
  store?: { id: number; name: string; storeLatitude?: number | null; storeLongitude?: number | null } | null;
  customerName?: string | null; customerPhone?: string | null; notes?: string | null;
  items: Array<{ id: number; name: string; quantity: number; pricePence: number; lineTotalPence?: number | null; isFreeItem?: boolean; refundedQuantity?: number; selectedOptions?: string | null; notes?: string | null }>;
};

type DriverAssignment = {
  id: number; driverId: number; driverName: string | null;
  driverPhone: string | null; vehicleType: string; claimedAt: string;
  pickedUpAt: string | null; deliveredAt: string | null;
  driverLat: number | null; driverLng: number | null;
};

type ChatMessage = {
  id: number; senderUserId: number; body: string; attachmentIds?: string[]; createdAt: string; senderName: string | null;
};

const STATUS_STEPS = ["confirmed", "preparing", "ready", "out_for_delivery", "delivered"];
const E2E_STATUS_STEPS = ["confirmed", "preparing", "ready", "collected", "at_shop", "ready_for_return", "returning", "delivered"];
/* "ready" means the food is made. What happens next depends entirely on whether
   the customer is collecting it or waiting at home for it, and this map did not
   know the difference — so a delivery customer was told their order was "Ready
   for pickup" and reasonably asked why, having paid for delivery. Mobile has
   always branched on this; the web tracker had not. */
const readyLabel = (fulfilmentType: string) =>
  fulfilmentType === "collection" ? "Ready for collection" : "Ready — driver on the way";

const STATUS_LABEL: Record<string, string> = {
  pending: "Pending", confirmed: "Confirmed", preparing: "Preparing",
  ready: "Ready", out_for_delivery: "On the way", delivered: "Delivered", cancelled: "Cancelled",
  collected: "Item collected — on the way to shop", at_shop: "At the shop — being worked on", ready_for_return: "Ready — return driver dispatched",
  returning: "On the way back to you",
};

const E2E_STATUS_DESCRIPTION: Record<string, string> = {
  confirmed:        "Your order is confirmed. A driver will be assigned to collect your item shortly.",
  preparing:        "A driver is heading to you. Please have your item packaged and ready for collection.",
  ready:            "A driver is heading to you. Please have your item packaged and ready for collection.",
  collected:        "Your item has been collected and is on the way to the shop.",
  at_shop:          "Your item is at the shop being worked on. You'll be notified when it's ready to return.",
  ready_for_return: "Your item is ready! A driver is on the way to bring it back to you.",
  returning:        "Your item is on its way back to you. The driver will deliver it shortly.",
};

function vehicleEmoji(type: string) {
  if (type === "car") return "";
  if (type === "scooter" || type === "moped") return "";
  if (type === "van") return "";
  return "";
}

const TIP_PRESETS = [100, 200, 300, 500];

type TipResult = { tipPence: number; grossTipPence: number; platformFeePence: number };

function TipPaymentForm({
  orderId, onSuccess,
}: {
  orderId: number;
  onSuccess: (result: TipResult) => void;
}) {
  const stripe = useStripeEl();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [payErr, setPayErr] = useState<string | null>(null);

  async function handlePay() {
    if (!stripe || !elements) return;
    setPaying(true);
    setPayErr(null);
    try {
      const submitResult = await elements.submit();
      if (submitResult.error) { setPayErr(submitResult.error.message ?? "Error"); return; }
      const confirmResult = await stripe.confirmPayment({
        elements,
        confirmParams: {},
        redirect: "if_required",
      });
      if (confirmResult.error) { setPayErr(confirmResult.error.message ?? "Payment failed"); return; }
      const piId = confirmResult.paymentIntent?.id;
      if (!piId) { setPayErr("Payment confirmation failed"); return; }
      const r = await fetch(`/api/order/orders/${orderId}/tip/confirm`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ paymentIntentId: piId }),
      });
      const data = await r.json() as { tipPence?: number; grossTipPence?: number; platformFeePence?: number; error?: string };
      if (!r.ok) { setPayErr(data.error ?? "Could not record tip"); return; }
      onSuccess({ tipPence: data.tipPence ?? 0, grossTipPence: data.grossTipPence ?? 0, platformFeePence: data.platformFeePence ?? 0 });
    } finally {
      setPaying(false);
    }
  }

  return (
    <div className="space-y-3 mt-3">
      <PaymentElement />
      {payErr && <p className="text-xs text-red-500">{payErr}</p>}
      <Button onClick={() => void handlePay()} disabled={paying} className="w-full bg-pink-500 hover:bg-pink-600 text-white">
        {paying ? "Processing…" : "Send tip"}
      </Button>
    </div>
  );
}

function TipDriverCard({ orderId, driverName, pledgedTipPence }: { orderId: number; driverName: string | null; pledgedTipPence?: number | null }) {
  const [selectedPence, setSelectedPence] = useState<number | null>(pledgedTipPence ?? null);
  const [customStr, setCustomStr] = useState("");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripeObj, setStripeObj] = useState<Stripe | null>(null);
  const [loading, setLoading] = useState(false);
  const [tipResult, setTipResult] = useState<TipResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const effectivePence = selectedPence === -1
    ? Math.round(Number(customStr) * 100)
    : (selectedPence ?? 0);

  async function handleAmountChosen(amountPence: number) {
    if (amountPence < 50) return;
    setErr(null);
    setLoading(true);
    try {
      const r = await fetch(`/api/order/orders/${orderId}/tip`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ amountPence }),
      });
      const data = await r.json() as { clientSecret?: string; publishableKey?: string; error?: string };
      if (!r.ok || !data.clientSecret || !data.publishableKey) {
        setErr(data.error ?? "Could not start tip payment");
        return;
      }
      const s = await loadStripe(data.publishableKey);
      setStripeObj(s);
      setClientSecret(data.clientSecret);
    } finally {
      setLoading(false);
    }
  }

  if (tipResult !== null) {
    return (
      <div className="flex items-center gap-3 bg-pink-50 border border-pink-200 rounded-[14px] px-4 py-4">
        <Heart size={18} className="fill-pink-400 text-pink-400 flex-shrink-0" />
        <div>
          <p className="font-bold text-pink-700 text-sm">Tip sent — thank you!</p>
          <p className="text-xs text-pink-600 mt-0.5">
            You sent {pence(tipResult.grossTipPence)} to {driverName ?? "your driver"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-pink-50 border border-pink-200 rounded-[14px] p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Heart size={16} className="text-pink-500 flex-shrink-0" />
        <h3 className="font-bold text-pink-800 text-base">Tip your driver</h3>
      </div>
      <p className="text-sm text-pink-700">
        Was {driverName ?? "your driver"} great? Leave them a tip — it goes straight to them.
      </p>

      {!clientSecret ? (
        <>
          <div className="grid grid-cols-4 gap-2">
            {TIP_PRESETS.map(p => (
              <button
                key={p}
                onClick={() => { setSelectedPence(p); setCustomStr(""); }}
                className={`py-2 rounded-xl border-2 text-sm font-bold transition-all ${
                  selectedPence === p
                    ? "border-pink-400 bg-pink-400 text-white"
                    : "border-pink-200 bg-white text-pink-700 hover:border-pink-300"
                }`}
              >
                £{(p / 100).toFixed(0)}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <div className="flex items-center border-2 border-pink-200 rounded-xl bg-white flex-1 px-3">
              <span className="text-pink-500 font-bold">£</span>
              <input
                type="number"
                min="0.50"
                max="20"
                step="0.50"
                placeholder="Custom"
                value={customStr}
                onChange={e => { setCustomStr(e.target.value); setSelectedPence(-1); }}
                className="flex-1 py-2 pl-1 text-sm focus:outline-none bg-transparent"
              />
            </div>
            <Button
              onClick={() => void handleAmountChosen(effectivePence)}
              disabled={loading || effectivePence < 50}
              className="bg-pink-500 hover:bg-pink-600 text-white px-5"
            >
              {loading ? "…" : "Next"}
            </Button>
          </div>
          {err && <p className="text-xs text-red-500">{err}</p>}
        </>
      ) : (
        stripeObj && clientSecret && (
          <Elements stripe={stripeObj} options={{ clientSecret, appearance: orderingPaymentAppearance }}>
            <TipPaymentForm orderId={orderId} onSuccess={result => setTipResult(result)} />
          </Elements>
        )
      )}
    </div>
  );
}

export default function OrderTrack() {
  const [, params] = useRoute<{ orderId: string }>("/order/track/:orderId");
  const { user } = useAuth();
  const orderId = params?.orderId ? Number(params.orderId) : null;

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [driverAssignment, setDriverAssignment] = useState<DriverAssignment | null | undefined>(undefined);
  const [customerHasRated, setCustomerHasRated] = useState(false);
  const [ratingPending, setRatingPending] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [storeRated, setStoreRated] = useState(false);
  const [storeRatingPending, setStoreRatingPending] = useState(0);
  const [storeRatingComment, setStoreRatingComment] = useState("");
  const [storeRatingSubmitting, setStoreRatingSubmitting] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [sending, setSending] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const orderPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  type AltOffer = { id: number; orderId: number; originalItemName: string; alternativeName: string; alternativePricePence: number; originalPricePence: number; expiresAt: string };
  const [altOffers, setAltOffers] = useState<AltOffer[]>([]);
  const [altOfferResponding, setAltOfferResponding] = useState<number | null>(null);
  const [altOfferError, setAltOfferError] = useState<string | null>(null);

  async function loadOrder() {
    if (!orderId) return;
    const r = await fetch(`/api/order/orders/${orderId}`, { headers: authHeaders() });
    if (!r.ok) { setError("Order not found"); setLoading(false); return; }
    const d = await r.json() as OrderDetail;
    setOrder(d);
    setLoading(false);
  }

  async function loadAlternatives() {
    if (!orderId) return;
    const r = await fetch(`/api/order/orders/${orderId}/alternatives`, { headers: authHeaders() });
    if (!r.ok) return;
    const d = await r.json() as { offers: AltOffer[] };
    setAltOffers(d.offers ?? []);
  }

  async function respondToAltOffer(offerId: number, accepted: boolean) {
    setAltOfferResponding(offerId);
    setAltOfferError(null);
    try {
      const r = await fetch(`/api/order/alternatives/${offerId}/respond`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ accepted }),
      });
      const d = await r.json() as { ok?: boolean; error?: string };
      if (!r.ok) { setAltOfferError(d.error ?? "Could not respond to offer"); return; }
      setAltOffers((prev) => prev.filter((o) => o.id !== offerId));
      void loadOrder();
    } catch {
      setAltOfferError("Network error. Please try again.");
    } finally {
      setAltOfferResponding(null);
    }
  }

  async function loadDriver() {
    if (!orderId) return;
    const r = await fetch(`/api/orders/${orderId}/driver`, { headers: authHeaders() });
    if (!r.ok) { setDriverAssignment(null); return; }
    const d = await r.json() as { assignment: DriverAssignment | null; customerHasRated?: boolean };
    setDriverAssignment(d.assignment);
    if (d.customerHasRated) setCustomerHasRated(true);
  }

  // Poll driver location while order is in transit (any leg)
  useEffect(() => {
    const isHub = order?.driverMode === "hub" && order?.fulfilmentType === "delivery";
    const inTransit = ["out_for_delivery", "collected", "returning"].includes(order?.status ?? "");
    if (!order || !inTransit || !isHub) return;
    const interval = setInterval(() => void loadDriver(), 15000);
    return () => clearInterval(interval);
  }, [order?.status, order?.driverMode, order?.fulfilmentType, orderId]);

  async function loadMessages() {
    if (!orderId) return;
    const r = await fetch(`/api/orders/${orderId}/driver-messages`, { headers: authHeaders() });
    if (!r.ok) return;
    const d = await r.json() as { items: ChatMessage[] };
    setMessages(d.items ?? []);
  }

  useEffect(() => {
    void loadOrder();
    void loadAlternatives();
    orderPollRef.current = setInterval(() => { void loadOrder(); void loadAlternatives(); }, 15000);
    return () => { if (orderPollRef.current) clearInterval(orderPollRef.current); };
  }, [orderId]);

  useEffect(() => {
    if (!order) return;
    const isHubDelivery = order.driverMode === "hub" && order.fulfilmentType === "delivery";
    // Always load assignment for hub-delivery orders — including delivered — so delivery time is available.
    if (isHubDelivery) {
      void loadDriver();
    }
  }, [order]);

  function openChat() {
    setShowChat(true);
    void loadMessages();
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => void loadMessages(), 5000);
  }

  function closeChat() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setShowChat(false);
  }

  async function sendMessage() {
    if (!orderId || !chatInput.trim() || sending || !driverAssignment) return;
    setSending(true);
    try {
      await fetch(`/api/orders/${orderId}/driver-messages`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ body: chatInput.trim(), recipientUserId: driverAssignment.driverId }),
      });
      setChatInput("");
      await loadMessages();
    } finally { setSending(false); }
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <Package size={40} className="mx-auto text-slate-300" />
          <p className="text-slate-600 font-medium">Please log in to track your order</p>
          <Link href="/login"><Button>Log in</Button></Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <p className="text-red-500 font-medium">{error ?? "Order not found"}</p>
          <Link href="/my-orders"><Button variant="outline">Back to My Orders</Button></Link>
        </div>
      </div>
    );
  }

  const isHubDelivery = order.driverMode === "hub" && order.fulfilmentType === "delivery";
  const isDone = order.status === "delivered" || order.status === "cancelled";
  const waitingForDriver = isHubDelivery && order.status === "ready" && driverAssignment === null;
  const hasDriver = isHubDelivery && driverAssignment != null;
  const isE2E = order.isEndToEndOrder === true;
  const steps = isE2E ? E2E_STATUS_STEPS : STATUS_STEPS;
  const currentStepIdx = steps.indexOf(order.status);

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      {/* Header */}
      <div className="bg-white text-[#1e3a5f] px-5 py-6 border-b border-slate-100">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-slate-500 text-xs mb-1">Order #{order.id}</p>
            <h1 className="font-bold text-2xl tracking-tight">{order.store?.name ?? "Order Tracking"}</h1>
          </div>
          <Link href="/my-orders">
            <button className="text-[#1e3a5f] text-sm underline">My Orders</button>
          </Link>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 pt-6 space-y-4">

        {/* Status progress */}
        {order.status !== "cancelled" && (
          <div className="bg-white rounded-[14px] border border-slate-100 p-5">
            <p className="text-xs font-semibold tracking-[1.3px] uppercase text-slate-500 mb-3">Your order</p>
            <h2 className="text-[32px] font-bold tracking-[-1px] leading-tight text-[#1e3a5f] mb-5">{order.status === "ready" ? readyLabel(order.fulfilmentType) : (STATUS_LABEL[order.status] ?? order.status)}</h2>
            <div className="flex gap-1.5 mb-6" aria-hidden="true">{steps.map((step, idx) => <span key={step} className={`h-1.5 flex-1 rounded-full ${idx <= currentStepIdx ? "bg-[#f0a500]" : "bg-slate-100"}`} />)}</div>
            <details><summary className="text-sm font-semibold text-[#1e3a5f] cursor-pointer mb-4">Order progress</summary>
            <div className="relative">
              {steps.map((step, idx) => {
                const done = idx <= currentStepIdx;
                const current = idx === currentStepIdx;
                return (
                  <div key={step} className="flex items-start gap-3 mb-4 last:mb-0">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${done ? "bg-primary border-primary" : "bg-white border-slate-200"}`}>
                        {done ? <CheckCircle2 size={16} className="text-white" /> : <span className="w-2 h-2 rounded-full bg-slate-300" />}
                      </div>
                      {idx < steps.length - 1 && (
                        <div className={`w-0.5 h-6 mt-1 ${idx < currentStepIdx ? "bg-primary" : "bg-slate-200"}`} />
                      )}
                    </div>
                    <div className="pt-1">
                      <p className={`text-sm font-semibold ${current ? "text-primary" : done ? "text-slate-700" : "text-slate-400"}`}>
                        {step === "ready" ? readyLabel(order.fulfilmentType) : (STATUS_LABEL[step] ?? step)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div></details>
          </div>
        )}

        {order.status === "cancelled" && (
          <div className="bg-red-50 border border-red-200 rounded-[14px] p-5 text-center">
            <p className="font-bold text-red-600">This order was cancelled</p>
          </div>
        )}

        {/* E2E contextual description */}
        {isE2E && !isDone && E2E_STATUS_DESCRIPTION[order.status] && (
          <div className="bg-violet-50 border border-violet-200 rounded-[14px] p-4 flex items-start gap-3">
            <Truck size={24} className="text-primary shrink-0" aria-hidden />
            <div>
              <p className="font-bold text-violet-800 text-sm mb-1">Collect &amp; Return Service</p>
              <p className="text-sm text-violet-700">{E2E_STATUS_DESCRIPTION[order.status]}</p>
              {(order.status === "confirmed" || order.status === "preparing" || order.status === "ready") && (
                <div className="mt-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-3 flex items-center gap-2">
                  <Package size={20} className="text-amber-700 shrink-0" aria-hidden />
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">Please package your item securely before the driver arrives for collection.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delivery code — shown to customer for active hub deliveries so driver can verify */}
        {isHubDelivery && order.deliveryCode && order.status !== "delivered" && order.status !== "cancelled" && (
          <DeliveryCodeCard
            code={order.deliveryCode}
            enRoute={order.status === "out_for_delivery"}
            size="lg"
          />
        )}

        {/* Delivered thank-you banner */}
        {order.status === "delivered" && driverAssignment?.deliveredAt && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-[14px] p-5 flex items-start gap-3">
            <CheckCircle2 size={22} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-emerald-800 text-base">Your order has been delivered!</p>
              <p className="text-sm text-emerald-700 mt-0.5">
                Delivered at {new Date(driverAssignment.deliveredAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                {driverAssignment.driverName ? ` by ${driverAssignment.driverName}` : ""}.
              </p>
              <p className="text-xs text-emerald-600 mt-1">Thank you for ordering with {order.store?.name ?? "us"} on Ayrshire Hub!</p>
            </div>
          </div>
        )}
        {order.status === "delivered" && !driverAssignment?.deliveredAt && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-[14px] p-5 flex items-center gap-3">
            <CheckCircle2 size={22} className="text-emerald-600 flex-shrink-0" />
            <p className="font-bold text-emerald-800">Your order has been delivered — enjoy!</p>
          </div>
        )}

        {/* Driver rating prompt */}
        {order.status === "delivered" && isHubDelivery && driverAssignment && driverAssignment.deliveredAt != null && !customerHasRated && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-[14px] p-5 space-y-3">
            <div>
              <h3 className="font-bold text-amber-800 dark:text-amber-300 text-base">Rate your driver</h3>
              <p className="text-sm text-amber-700 mt-0.5">How was {driverAssignment.driverName ?? "your driver"}?</p>
            </div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button key={star} onClick={() => setRatingPending(star)} className="focus:outline-none hover:scale-110 transition-transform">
                  <Star size={28} className={star <= ratingPending ? "fill-amber-400 text-amber-400" : "text-gray-300"} />
                </button>
              ))}
            </div>
            {ratingPending > 0 && (
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Add a comment (optional)…"
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  maxLength={200}
                  className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
                <Button
                  onClick={async () => {
                    if (ratingSubmitting || !driverAssignment || !orderId) return;
                    setRatingSubmitting(true);
                    try {
                      const r = await fetch(`/api/drivers/${driverAssignment.driverId}/ratings`, {
                        method: "POST", headers: authHeaders(),
                        body: JSON.stringify({ orderId, rating: ratingPending, comment: ratingComment || null }),
                      });
                      if (r.ok) setCustomerHasRated(true);
                    } finally {
                      setRatingSubmitting(false);
                    }
                  }}
                  disabled={ratingSubmitting}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                >
                  {ratingSubmitting ? "Submitting…" : "Submit Rating"}
                </Button>
              </div>
            )}
          </div>
        )}
        {order.status === "delivered" && isHubDelivery && driverAssignment && customerHasRated && (
          <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl px-4 py-3">
            <Star size={16} className="fill-amber-400 text-amber-400" />
            <span className="text-sm text-amber-700 font-medium">Thanks for rating your driver!</span>
          </div>
        )}

        {/* Tip your driver */}
        {order.status === "delivered" && isHubDelivery && driverAssignment?.deliveredAt && (
          <TipDriverCard orderId={order.id} driverName={driverAssignment.driverName} pledgedTipPence={order.pledgedTipPence} />
        )}

        {/* Store rating prompt */}
        {order.status === "delivered" && order.store && !storeRated && (
          <div className="bg-white border border-slate-200 rounded-[14px] p-5 space-y-3">
            <div>
              <h3 className="font-bold text-slate-800 text-base">Rate your order from {order.store.name}</h3>
              <p className="text-sm text-slate-500 mt-0.5">How was the food and service?</p>
            </div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button key={star} onClick={() => setStoreRatingPending(star)} className="focus:outline-none hover:scale-110 transition-transform">
                  <Star size={28} className={star <= storeRatingPending ? "fill-primary text-primary" : "text-gray-300"} />
                </button>
              ))}
            </div>
            {storeRatingPending > 0 && (
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Tell us about your experience (optional)…"
                  value={storeRatingComment}
                  onChange={(e) => setStoreRatingComment(e.target.value)}
                  maxLength={300}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <Button
                  onClick={async () => {
                    if (storeRatingSubmitting || !order.store || storeRatingPending < 1) return;
                    setStoreRatingSubmitting(true);
                    try {
                      const reviewerName = (user as { displayName?: string; username?: string; email?: string } | null)?.displayName ?? (user as { username?: string; email?: string } | null)?.username ?? (user as { email?: string } | null)?.email ?? "Customer";
                      const res = await fetch("/api/reviews", {
                        method: "POST",
                        headers: authHeaders(),
                        body: JSON.stringify({ reviewerName, targetType: "order_store", targetId: order.store.id, rating: storeRatingPending, reviewText: storeRatingComment || `${storeRatingPending} star order rating` }),
                      });
                      if (!res.ok) throw new Error("Review failed");
                      setStoreRated(true);
                    } finally {
                      setStoreRatingSubmitting(false);
                    }
                  }}
                  disabled={storeRatingSubmitting}
                  className="w-full"
                >
                  {storeRatingSubmitting ? "Submitting…" : "Submit Review"}
                </Button>
              </div>
            )}
          </div>
        )}
        {order.status === "delivered" && order.store && storeRated && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            <Star size={16} className="fill-primary text-primary" />
            <span className="text-sm text-emerald-700 font-medium">Thanks for rating {order.store.name}!</span>
          </div>
        )}

        {/* Waiting for driver badge */}
        {waitingForDriver && (
          <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-700">Waiting for a driver</p>
              <p className="text-xs text-amber-600">A hub driver will pick up your order shortly</p>
            </div>
          </div>
        )}

        {/* Alternative offer banners */}
        {altOffers.map((offer) => (
          <div key={offer.id} className="bg-amber-50 border border-amber-200 rounded-[14px] p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Package size={16} className="text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-amber-900 text-sm">Replacement item suggested</p>
                <p className="text-xs text-amber-700 mt-0.5">The store cannot fulfil your original item and has suggested a replacement.</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-amber-100 p-3 space-y-2 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>Original: <span className="text-slate-800 font-medium">{offer.originalItemName}</span></span>
                <span>{pence(offer.originalPricePence)}</span>
              </div>
              <div className="flex justify-between text-emerald-700">
                <span>Replacement: <span className="font-semibold">{offer.alternativeName}</span></span>
                <span>{pence(offer.alternativePricePence)}</span>
              </div>
            </div>
            {altOfferError && altOfferResponding === offer.id && (
              <p className="text-xs text-red-500">{altOfferError}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => void respondToAltOffer(offer.id, false)}
                disabled={altOfferResponding === offer.id}
                className="flex-1 py-2.5 text-sm font-semibold rounded-xl bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                No thanks
              </button>
              <button
                onClick={() => void respondToAltOffer(offer.id, true)}
                disabled={altOfferResponding === offer.id}
                className="flex-1 py-2.5 text-sm font-semibold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {altOfferResponding === offer.id ? "…" : "Accept"}
              </button>
            </div>
          </div>
        ))}

        {/* Your driver panel */}
        {hasDriver && driverAssignment && (
          <div className="bg-white rounded-[14px] border border-blue-100 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Truck size={16} className="text-blue-600" />
              <h2 className="font-bold text-foreground">Your Driver</h2>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-lg font-bold text-slate-800">
                  {vehicleEmoji(driverAssignment.vehicleType)} {driverAssignment.driverName ?? "Your driver"}
                </p>
                <p className="text-sm text-slate-500 mt-0.5">
                  {driverAssignment.deliveredAt
                    ? "Delivered your order"
                    : driverAssignment.pickedUpAt
                    ? "On the way to you"
                    : "Heading to collect your order"}
                </p>
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <Clock size={10} />
                  Claimed {new Date(driverAssignment.claimedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              <Button
                onClick={openChat}
                className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
              >
                <MessageSquare size={14} /> Chat
              </Button>
            </div>

            {/* Live driver location map */}
            {["out_for_delivery", "collected", "returning"].includes(order.status) && driverAssignment.driverLat != null && driverAssignment.driverLng != null && (
              <div className="mt-4 rounded-xl overflow-hidden border border-blue-100">
                <DriverLocationMap
                  lat={driverAssignment.driverLat}
                  lng={driverAssignment.driverLng}
                  storeLat={order.store?.storeLatitude}
                  storeLng={order.store?.storeLongitude}
                  height={200}
                />
              </div>
            )}
            {["out_for_delivery", "collected", "returning"].includes(order.status) && (driverAssignment.driverLat == null || driverAssignment.driverLng == null) && (
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                Waiting for driver location…
              </div>
            )}
          </div>
        )}

        {/* Order summary */}
        <div className="bg-white rounded-[14px] border border-slate-100 p-5">
          <h2 className="font-bold text-foreground mb-3">Order Summary</h2>
          <div className="space-y-1.5 mb-3">
            {order.items.map(item => {
              const fullyRefunded = (item.refundedQuantity ?? 0) >= item.quantity;
              let optionLabel = "";
              try {
                const opts = item.selectedOptions ? (JSON.parse(item.selectedOptions) as Array<{ optionName?: string; groupName?: string }>) : [];
                optionLabel = opts.map((op) => op.groupName ? `${op.groupName}: ${op.optionName ?? ""}` : (op.optionName ?? "")).filter(Boolean).join(", ");
              } catch { /* */ }
              return (
                <div key={item.id} className={fullyRefunded ? "opacity-50" : ""}>
                  <div className="flex justify-between text-sm items-center">
                    <span className={`text-slate-700 ${fullyRefunded ? "line-through" : ""}`}>{item.quantity}× {item.name}</span>
                    {fullyRefunded
                      ? <span className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">Removed</span>
                      : <span className="text-slate-600">{item.isFreeItem ? "FREE" : pence(customerItemTotal(item))}</span>
                    }
                  </div>
                  {!!optionLabel && <p className="text-xs text-slate-400 ml-2">{optionLabel}</p>}
                  {!!item.notes && <p className="text-xs text-slate-400 italic ml-2">Note: {item.notes}</p>}
                </div>
              );
            })}
          </div>
          <CustomerOrderSummary order={order} />
          {order.deliveryAddress && (
            <p className="text-xs text-slate-500 mt-3"> {order.deliveryAddress}</p>
          )}
          {order.customerName && (
            <p className="text-xs text-slate-500 mt-1"> {order.customerName}{order.customerPhone ? ` · ${order.customerPhone}` : ""}</p>
          )}
          {order.notes && (
            <p className="text-xs text-slate-500 mt-1"> {order.notes}</p>
          )}
        </div>
      {/* Post-delivery CTAs */}
      {order.status === "delivered" && (
        <div className="bg-white rounded-[14px] border border-slate-100 p-5 space-y-3">
          <h2 className="font-bold text-slate-800">What would you like to do next?</h2>
          <div className="flex flex-col gap-2">
            {order.store && (
              <Link href={`/order-stores/${order.store.id}`}>
                <Button className="w-full gap-2">
                   Order again from {order.store.name}
                </Button>
              </Link>
            )}
            <Link href="/order-stores">
              <Button variant="outline" className="w-full">Browse all shops</Button>
            </Link>
            <Link href="/my-orders">
              <Button variant="ghost" className="w-full text-slate-500">View all my orders</Button>
            </Link>
          </div>
        </div>
      )}

      </div>

      {/* Chat modal */}
      {showChat && (
        <Dialog open onOpenChange={(open) => { if (!open) closeChat(); }}>
          <DialogContent aria-describedby={undefined} className="bg-white w-full sm:max-w-lg rounded-3xl p-0 gap-0 flex flex-col max-h-[90dvh] [&>button:last-child]:hidden">
            <div className="flex items-center justify-between px-5 py-5 border-b border-slate-100">
              <DialogTitle className="font-bold text-xl tracking-tight text-[#1e3a5f]">Chat with your driver</DialogTitle>
              <button onClick={closeChat} aria-label="Close driver chat" className="w-11 h-11 grid place-items-center rounded-full bg-slate-100"><X size={20} /></button>
            </div>

            {/* Quick replies */}
            <div className="flex gap-2 flex-wrap px-4 py-3 border-b border-slate-100">
              {["What's your ETA?", "Please leave at door", "I'll meet you outside"].map(q => (
                <button key={q} onClick={() => setChatInput(q)}
                  className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 text-xs font-medium px-2.5 py-1.5 rounded-full hover:bg-blue-100 transition-colors">
                  {q}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 gap-2 text-slate-400">
                  <MessageSquare size={32} className="opacity-30" />
                  <p className="text-sm">No messages yet</p>
                </div>
              ) : messages.map(msg => (
                <div key={msg.id} className="space-y-0.5">
                  <p className="text-xs font-semibold text-slate-500">{msg.senderName ?? "Unknown"}</p>
                  <div className="bg-slate-50 border border-slate-100 rounded-xl px-3 py-2 text-sm text-slate-800"><MessageImages ids={msg.attachmentIds}/>{msg.body}</div>
                  <p className="text-[10px] text-slate-400">{new Date(msg.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              ))}
            </div>
            <div className="px-4 pb-4 pt-2 border-t border-slate-100 flex gap-2">
              {driverAssignment&&<ChatImageComposer key={`${orderId}:${driverAssignment.driverId}`} endpoint={`/api/orders/${orderId}/driver-messages`} recipientUserId={driverAssignment.driverId} onSent={loadMessages}/>}
<input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type a message…"
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void sendMessage(); } }}
              />
              <Button onClick={() => void sendMessage()} disabled={sending || !chatInput.trim()} className="gap-1.5">
                <Send size={14} /> Send
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
